/**
 * failedProductReason.ts
 *
 * Maps the server-side error code on a failed PRODUCT_CARD send to a
 * friendly user-facing reason text. Spec: `docs/prd/tiktok-chat/features/
 * product-card-presentation.v2.html` slide 22 — the failed-card body
 * shows a friendly reason, not the raw error code.
 *
 * Fallback chain: known code → raw `errorMessage` from server → generic.
 */

const REASONS: Record<string, string> = {
  INVALID_PRODUCT_ID: 'Product ID format is invalid.',
  PRODUCT_NOT_FOUND:
    "This product was not found in the buyer's marketplace. It may have been deleted, or it belongs to a different shop.",
  PRODUCT_ACCESS_DENIED:
    "This shop doesn't have permission to send this product.",
  MISSING_ACCESS_TOKEN:
    'Your TikTok connection needs to be reconnected.',
  EXPIRED_ACCESS_TOKEN:
    'Your TikTok connection expired. Please reconnect in Settings.',
  RATE_LIMIT_EXCEEDED:
    'TikTok rate limit reached. Try again in a minute.',
  SERVICE_UNAVAILABLE:
    'TikTok service is temporarily unavailable. Try again shortly.',
  NETWORK_TIMEOUT: "Couldn't reach TikTok in time. Try again.",
  // Codes the server emits today that aren't in the PRD-friendly set.
  SEND_ERROR: 'TikTok rejected this product send.',
  INVALID_TYPE: "Message type isn't supported.",
}

const GENERIC_FALLBACK =
  "Couldn't send this product. Try again or pick another product."

/**
 * Resolve a friendly reason string for the failed-product-card UI.
 *
 * @param errorCode  Server-emitted code (e.g. `INVALID_PRODUCT_ID`)
 * @param rawMessage Server-emitted raw `errorMessage` — used as a fallback
 *                   when `errorCode` is missing or unmapped
 */
export function mapFailedProductReason(
  errorCode?: string | null,
  rawMessage?: string | null
): string {
  if (errorCode && REASONS[errorCode]) return REASONS[errorCode]
  if (rawMessage && rawMessage.trim().length > 0) return rawMessage
  return GENERIC_FALLBACK
}

export const __testing = { REASONS, GENERIC_FALLBACK }
